/**
 * What the maker receives.
 *
 * This is the outcome the whole tool exists to produce, and it is deliberately NOT
 * manufacturing data: no panel sizes, no drilling, no DXF. The maker builds in their own
 * system. What they need from here is a design their customer has agreed to, in enough
 * detail to price without a phone call -- every cabinet, its size, its door, its finish, its
 * handle -- and a picture the customer will recognise as the thing they said yes to.
 *
 * Plain data out. The sheet that renders it and the file that gets emailed are two different
 * problems and both of them read this.
 */

import { review } from './guidelines.js'

/** One line per cabinet, in the order they stand in the kitchen. */
export function schedule(plan, maker) {
  const pick = (list, id) => list.find((x) => x.id === id) ?? list[0]
  const style = pick(maker.doorStyles, plan.doorStyleId)
  const colour = pick(maker.colours, plan.colourId)
  const handle = pick(maker.handles, plan.handleId)
  const bench = pick(maker.benchtops, plan.benchtopId)
  const splash = pick(maker.splashbacks, plan.splashbackId)
  // The maker orders a door by profile AND colour, so the schedule names both.
  const door = { name: `${style.name} ${colour.name}` }

  const lines = []
  for (const run of plan.runs) {
    for (const item of run.items) {
      if (item.gap) continue
      const spec = maker.cabinets.find((c) => c.id === item.specId)
      if (item.filler) {
        lines.push({
          code: 'FILL', name: 'Filler panel', run: run.id, kind: run.kind,
          w: Math.round(item.w), h: null, d: null,
          door: door?.name ?? '', handle: '', band: '',
        })
        continue
      }
      if (!spec) continue
      lines.push({
        code: spec.id.toUpperCase(),
        name: spec.name,
        run: run.id,
        kind: run.kind,
        w: Math.round(item.w),
        h: spec.kind === 'wall' ? maker.limits.wallHeight
          : spec.kind === 'tall' ? spec.h : maker.limits.baseHeight,
        d: spec.kind === 'wall' ? maker.limits.wallDepth : maker.limits.baseDepth,
        door: door?.name ?? '',
        handle: spec.drawers || spec.doors ? (handle?.name ?? '') : '',
        // Marked, because a stretched cabinet is the one line a maker must not skim past:
        // it is not a catalogue size and it is why the run adds up.
        stretched: item.stretched ? `made at ${Math.round(item.w)}mm` : '',
        band: spec.band ?? '',
      })
    }
  }

  return {
    maker: maker.name,
    when: new Date().toISOString().slice(0, 10),
    finishes: {
      doors: door.name,
      style: style.name,
      colour: colour.name,
      benchtop: bench?.name ?? '',
      splashback: splash?.colour ? splash.name : `${bench?.name} (matched)`,
      handles: handle?.name ?? '',
    },
    counts: {
      cabinets: lines.filter((l) => l.code !== 'FILL').length,
      fillers: lines.filter((l) => l.code === 'FILL').length,
      runs: plan.runs.length,
    },
    /** Metres of benchtop, which is how a maker prices stone and the first thing they ask. */
    benchtopMetres: +(plan.runs
      .filter((r) => r.kind === 'base')
      .reduce((n, r) => n + r.items.reduce((m, i) => m + i.w, 0), 0) / 1000).toFixed(2),
    lines,
    /**
     * What a designer would have said. Faults are why sign-off waited; advice is what the
     * homeowner heard and decided about. The maker gets both, because "they were told" is
     * worth having in writing.
     */
    notes: review(plan, maker).map((f) => ({ level: f.level, text: f.text })),
  }
}

/**
 * The same thing as a file the maker can open in a spreadsheet.
 *
 * CSV rather than anything cleverer because every kitchen maker in the country has Excel and
 * none of them has our importer.
 */
export function toCsv(s) {
  const head = ['Code', 'Cabinet', 'Run', 'Type', 'Width', 'Height', 'Depth',
                'Door', 'Handle', 'Note']
  const rows = s.lines.map((l) => [
    l.code, l.name, l.run, l.kind, l.w, l.h ?? '', l.d ?? '',
    l.door, l.handle, l.stretched ?? '',
  ])
  const esc = (v) => {
    const t = String(v ?? '')
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t
  }
  return [
    [`${s.maker} - kitchen schedule`, s.when].map(esc).join(','),
    [`Doors: ${s.finishes.style} profile in ${s.finishes.colour}`].map(esc).join(','),
    [`Benchtop: ${s.finishes.benchtop} (${s.benchtopMetres}m)`].map(esc).join(','),
    [`Splashback: ${s.finishes.splashback}`].map(esc).join(','),
    [`Handles: ${s.finishes.handles}`].map(esc).join(','),
    '',
    head.join(','),
    ...rows.map((r) => r.map(esc).join(',')),
    ...(s.notes?.length ? ['', 'Design notes', ...s.notes.map((n) =>
      [n.level === 'fault' ? 'Must fix' : 'Advice', n.text].map(esc).join(','))] : []),
  ].join('\n')
}
